// Saturn/Uranus ring shader with proper UV mapping and transparency

import { OKLAB_MIX_GLSL } from './oklab.js';

export const ringVertexShader = `
  varying vec2 vUv;
  varying vec3 vPosition;
  varying vec3 vWorldPosition;
  varying vec3 vNormal;

  void main() {
    vUv = uv;
    vNormal = normalize(normalMatrix * normal);
    vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
    vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const ringFragmentShader = `
  uniform sampler2D ringTexture;
  uniform vec3 planetPosition;
  uniform float planetRadius;
  uniform vec3 sunPosition;
  uniform float innerRadius;
  uniform float outerRadius;
  uniform float opacity;

  varying vec2 vUv;
  varying vec3 vPosition;
  varying vec3 vWorldPosition;
  varying vec3 vNormal;

  void main() {
    // Calculate radial distance from center for UV mapping
    // The ring geometry's UVs need to be mapped radially
    vec2 centeredUv = vUv - 0.5;
    float dist = length(centeredUv) * 2.0;

    // Remap to 0-1 range based on inner/outer radius
    float normalizedDist = (dist - 0.0) / (1.0 - 0.0);

    // Sample texture using radial coordinate
    vec2 radialUv = vec2(normalizedDist, 0.5);
    vec4 texColor = texture2D(ringTexture, radialUv);

    // Calculate shadow from planet
    vec3 toSun = normalize(sunPosition - vWorldPosition);
    vec3 toPlanet = planetPosition - vWorldPosition;
    float distToPlanet = length(toPlanet);

    // Project point onto sun ray to check if in shadow
    float shadowCheck = dot(normalize(toPlanet), -toSun);
    float inShadow = 0.0;

    if (shadowCheck > 0.0) {
      // Point is on the planet's shadow side
      vec3 projectedPoint = vWorldPosition + toSun * dot(toPlanet, toSun);
      float distFromAxis = length(planetPosition - projectedPoint);

      if (distFromAxis < planetRadius * 1.1) {
        inShadow = smoothstep(planetRadius * 1.1, planetRadius * 0.9, distFromAxis);
      }
    }

    // Apply lighting
    float sunLight = max(dot(vNormal, toSun), 0.0) * 0.5 + 0.5;
    sunLight *= (1.0 - inShadow * 0.7);

    // Ring color with lighting
    vec3 finalColor = texColor.rgb * sunLight;

    // Alpha from texture and overall opacity
    float alpha = texColor.a * opacity;

    // Discard very transparent fragments
    if (alpha < 0.01) discard;

    gl_FragColor = vec4(finalColor, alpha);
  }
`;

// Saturn ring shader with analytic planet shadow cast onto the rings.
// The shadow is a proper CYLINDER of radius planetRadius extending from
// the planet away from the sun along a SINGLE axis (sun → planet
// direction). For each ring fragment we test whether it falls inside
// that cylinder. Previous implementation used a per-fragment sun-to-
// fragment ray, which gave curved scalloped shadow edges; a cylinder
// has straight parallel edges where it intersects the flat ring plane,
// which is what Cassini imagery actually shows.
export const saturnRingVertexShader = `
  varying vec2 vUv;
  varying vec3 vWorldPosition;
  // Ring-local planar coordinates so the fragment shader can compute
  // an azimuthal angle that's anchored to the ring (as opposed to
  // world space, where the ring's tilt + planet rotation would smear
  // the angle). RingGeometry lays the ring in the local XY plane, so
  // position.xy is the in-plane coordinate directly.
  varying vec2 vRingPlanar;
  // World-space ring-plane normal. RingGeometry lies in its local XY
  // plane, so local +Z is the plane normal; the fragment shader uses
  // it to tell the sun-lit face from the unlit face.
  varying vec3 vPlaneNormal;

  void main() {
    vUv = uv;
    vRingPlanar = position.xy;
    vPlaneNormal = normalize(mat3(modelMatrix) * vec3(0.0, 0.0, 1.0));
    vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const saturnRingFragmentShader = `
  // Light left in the planet's shadow: 22% for Saturn (below); the Earth's
  // rings (src/earthrings.js) define their own.
  #ifndef RING_SHADOW_FLOOR
  #define RING_SHADOW_FLOOR 0.22
  #endif
  uniform sampler2D ringTexture;
  uniform vec3 sunPosition;
  uniform vec3 planetCenter;
  uniform float planetRadius;
  uniform float opacity;
  // Spoke uniforms. spokeTime is wall-clock seconds (drives the
  // angular phase + lifecycle); spokeStrength = 0 disables the
  // effect entirely so non-Saturn rings using this shader stay clean.
  uniform float spokeTime;
  uniform float spokeStrength;
  // 1: the strip is Cassini's (alpha = 1 - exp(-tau) with the real gaps), so
  // the procedural divisions and the brightness-as-depth proxy stand aside.
  uniform float realStrip;
  // Per-planet sunlight multiplier (shared uniform contract; main.js
  // wires it). <= 0.001 means "unwired" → fall back to 1.0.
  uniform float uSunIntensity;
  // Sun's angular radius as seen from the planet (radians), wired per
  // frame. Drives the physical penumbra width of the planet's shadow
  // on the rings. 0 = unwired → legacy fixed-width edge.
  uniform float uSunAngularRadius;

  varying vec2 vUv;
  varying vec3 vWorldPosition;
  varying vec2 vRingPlanar;
  varying vec3 vPlaneNormal;

  // Smooth pulse: 1 inside [lo, hi] with edge-width transitions, 0 outside.
  float ringPulse(float x, float lo, float hi, float edge) {
    return smoothstep(lo - edge, lo + edge, x)
         * (1.0 - smoothstep(hi - edge, hi + edge, x));
  }

  void main() {
    vec4 texColor = texture2D(ringTexture, vUv);

    // Real Saturn ring structure as a procedural alpha mask. UV.x runs
    // from inner edge (1.15 r_sat) to outer edge (2.29 r_sat) given our
    // ringInner/Outer values. Major features mapped into that span:
    //   C ring  (faint, inner, ~UV 0.0-0.33)
    //   B ring  (densest, ~UV 0.33-0.70)
    //   Cassini Division  (UV ~0.71-0.77, the famous gap visible from Earth)
    //   A ring  (~UV 0.77-0.99)
    //   Encke Gap  (UV ~0.94, thin dark line inside the A ring)
    // Multiplying the texture alpha by this mask makes the divisions
    // visible no matter how flat the source texture is.
    float radial = vUv.x;
    float divisions = 1.0;
    if (realStrip < 0.5) {
      // C ring inner faintness (semi-transparent inner band).
      divisions *= mix(0.55, 1.0, smoothstep(0.0, 0.32, radial));
      // Cassini Division (drop alpha to ~7% of normal across the gap).
      divisions *= 1.0 - 0.93 * ringPulse(radial, 0.715, 0.770, 0.006);
      // Encke Gap (thin, narrow drop).
      divisions *= 1.0 - 0.80 * ringPulse(radial, 0.937, 0.948, 0.002);
      // Outer A-ring soft fade so the outer edge feathers out.
      divisions *= 1.0 - smoothstep(0.985, 1.0, radial);
    }

    // Shadow cylinder axis: single direction from sun to planet. Every
    // ring fragment uses the same axis, so the shadow region has straight
    // parallel edges where the cylinder cuts the ring plane.
    //
    // Penumbra width is physical: the sun's angular radius (uniform,
    // sunRadius / planet-sun distance, wired per frame) makes the
    // shadow edge fan out by t * angularRadius the further the ring
    // point sits behind the planet — Cassini shadow edges are visibly
    // softer at the far ansa than where the shadow leaves the limb.
    // Falls back to the old fixed 1.5%-of-radius band when unwired.
    float shadowFactor = 1.0;
    vec3 shadowAxisVector = planetCenter - sunPosition;
    float shadowAxisLength = length(shadowAxisVector);
    if (shadowAxisLength > 0.0001) {
      vec3 cylAxis = shadowAxisVector / shadowAxisLength;
      vec3 planetToFrag = vWorldPosition - planetCenter;
      float t = dot(planetToFrag, cylAxis);
      vec3 perp = planetToFrag - cylAxis * t;
      float perpDist = length(perp);
      if (t > 0.0) {
        float halfW = uSunAngularRadius > 0.0
          ? max(planetRadius * 0.0125, t * uSunAngularRadius)
          : planetRadius * 0.0125;
        shadowFactor = smoothstep(planetRadius - halfW, planetRadius + halfW, perpDist);
      }
    }

    // Inside the umbra, ring particles get enough back-scattered light
    // from Saturn's atmosphere and from outside-umbra ring material that
    // the shadow isn't pitch black. 22% ambient matches Cassini ISS
    // exposures of the shadow region reasonably well.
    float sunI = uSunIntensity > 0.001 ? uSunIntensity : 1.0;
    vec3 finalColor = texColor.rgb * (RING_SHADOW_FLOOR + shadowFactor * (1.0 - RING_SHADOW_FLOOR)) * sunI;

    // Forward-scatter glow. When the camera looks AT the rings from
    // the side opposite the sun (sun behind the rings as seen from
    // camera), the icy ring particles forward-scatter sunlight and
    // glow brighter than they do when lit from the front. Cassini's
    // "Day The Earth Smiled" image is the signature: Saturn fully
    // back-lit, the rings form a luminous halo around the planet.
    //
    // Detect: dot(viewDir, sunDir) < 0 means sun-and-camera are on
    // opposite sides of the ring point. Strongest at grazing
    // anti-sun angles; tapers off as camera moves to the sun side.
    vec3 viewDir = normalize(cameraPosition - vWorldPosition);
    vec3 sunDirRing = normalize(sunPosition - vWorldPosition);

    // Lit vs unlit ring face. sunSide / viewSide are the signed sides
    // of the ring plane the sun and camera each sit on; when their
    // product is negative the camera is looking at the face the sun
    // does NOT hit, so direct reflection drops to ~35% and the
    // transmission / forward-scatter terms below carry the brightness
    // (that's what real unlit-face Cassini views show). Smoothstep
    // over a narrow band around zero so there's no pop as the camera
    // crosses the ring plane.
    float sunSide = dot(sunDirRing, vPlaneNormal);
    float viewSide = dot(viewDir, vPlaneNormal);
    float unlitFace = 1.0 - smoothstep(-0.05, 0.05, sunSide * viewSide);
  #ifdef RING_PHOTOMETRY
    // Saturn: the unlit face against the lit one by single scattering in a
    // layer of the strip's optical depth (Chandrasekhar 1960; as used for
    // Saturn's rings by Cuzzi et al. 1984, in Planetary Rings): reflected
    // R = mu0 / (mu + mu0) (1 - e^(-tau (1/mu + 1/mu0))), transmitted
    // T = mu0 / (mu - mu0) (e^(-tau/mu) - e^(-tau/mu0)), with mu and mu0 the
    // sines of the view's and the Sun's heights over the plane. The lit face
    // is drawn as the strip, so the unlit one is T / R of it: the dense B ring
    // goes nearly black, the thin C ring, Cassini Division and A ring stay
    // nearly as bright, as Cassini saw them from below.
    float tauP = -log(max(1.0 - texColor.a, 0.0001));
    float muV = max(abs(viewSide), 0.02);
    float muS = max(abs(sunSide), 0.02);
    float reflR = muS / (muV + muS) * (1.0 - exp(-tauP * (1.0 / muV + 1.0 / muS)));
    float dMu = muV - muS;
    float transT = abs(dMu) < 1e-3
      ? tauP / muS * exp(-tauP / muS)
      : muS / dMu * (exp(-tauP / muV) - exp(-tauP / muS));
    finalColor *= mix(1.0, clamp(transT / max(reflR, 1e-4), 0.0, 1.5), unlitFace);
  #else
    finalColor *= mix(1.0, 0.35, unlitFace);
  #endif

    // The strip's alpha is face on (1 - exp(-tau)). With RING_SLANT the line
    // of sight crosses the layer along 1 / |mu| of its thickness, so the
    // rings close up toward edge on: the Earth's (src/earthrings.js), seen
    // from the ground a few degrees off their plane. Saturn keeps the face-on
    // alpha its look was approved with.
  #ifdef RING_SLANT
    float layerAlpha = 1.0 - pow(max(1.0 - texColor.a, 0.0), 1.0 / max(abs(viewSide), 0.02));
  #else
    float layerAlpha = texColor.a;
  #endif

    float backLight = max(-dot(viewDir, sunDirRing), 0.0);
    // Sharp forward-scatter peak (high cosTheta exponent), muted
    // floor so the glow only kicks in when the sun is genuinely
    // behind the rings from the camera's perspective.
    float forwardScatter = pow(backLight, 2.6);

    // Optical-thickness inversion for the backlit case. Texture
    // luminance is a reasonable proxy for ring particle density —
    // bright pixels in the front-lit Cassini source map onto the
    // densest material (B ring, optical depth τ ≈ 1-2) and dim
    // pixels onto the thin C ring / Cassini Division (τ ≈ 0.05-0.3).
    // Beer-Lambert e^(-τ) gives the fraction of sunlight that
    // transmits straight through the ring layer to the camera.
    //
    // Real backlit Saturn (the Cassini "In Saturn's Shadow" image)
    // shows clear inversion: thin rings glow brilliantly while the
    // dense B ring goes near-silhouette black. We reproduce that by
    // (a) attenuating forward-scatter contribution by transmission so
    // dense bands don't glow, and (b) scaling the base ring color by
    // transmission in proportion to how backlit the geometry is, so
    // dense bands darken into a silhouette while front-lit views
    // stay untouched.
    float tau = realStrip > 0.5
      ? -log(max(1.0 - texColor.a, 0.0001))
      : dot(texColor.rgb, vec3(0.2126, 0.7152, 0.0722));
    float transmission = realStrip > 0.5 ? exp(-tau) : exp(-tau * 3.2);

    // Warm ice colour, tracks shadow factor so the glow doesn't
    // override the analytic planet-shadow band — a forward-lit
    // ring particle inside Saturn's umbra still receives no light.
    // Multiplied by transmission so the densest B ring stops
    // contributing forward scatter the way real optically-thick
    // material does.
    vec3 forwardScatterColour = vec3(1.0, 0.92, 0.78) * forwardScatter * 0.85 * shadowFactor;
    finalColor += forwardScatterColour * transmission * texColor.a * sunI;

    // Backlit darkening of the base ring color. Front-lit
    // (forwardScatter ≈ 0): no change. Fully backlit
    // (forwardScatter ≈ 1): base color scaled by transmission, so
    // the dense B ring drops to near-silhouette while thin material
    // stays close to its full brightness.
    finalColor *= mix(1.0, transmission, forwardScatter);

    // Ring spokes. Voyager and Cassini repeatedly observed dark
    // radial streaks crossing the B ring — short-lived (minutes to
    // hours) features now thought to be electrostatically-charged
    // dust hovering above the ring plane near the planet's shadow
    // boundary. We reproduce the LOOK procedurally: rare azimuthal
    // dark bands gated to the B ring radial range, drifting with
    // their own phase rather than corotating cleanly so they read as
    // alive rather than baked into the texture.
    if (spokeStrength > 0.001) {
      float ringAngle = atan(vRingPlanar.y, vRingPlanar.x);
      // Restrict spokes to the B ring radial range (~UV 0.32-0.68
      // in our setup). Smooth ramps at both edges so the spokes
      // fade into the C ring and Cassini Division rather than
      // cutting hard.
      float spokeRadialMask = smoothstep(0.32, 0.40, vUv.x)
                            * (1.0 - smoothstep(0.62, 0.70, vUv.x));
      // Multi-frequency angular noise so the spoke pattern is
      // non-uniform around the ring rather than a perfect sine wave.
      float spokeAng =
          cos(ringAngle *  6.0 + spokeTime * 0.30)
        + cos(ringAngle * 10.0 + spokeTime * 0.51 + 1.7) * 0.7
        + cos(ringAngle * 15.0 - spokeTime * 0.42 + 3.1) * 0.5;
      // Threshold for discrete dark patches; values above the
      // threshold become spokes.
      float spokes = smoothstep(0.85, 1.7, spokeAng) * spokeRadialMask;
      // Slow lifecycle: each angular sector pulses in and out on a
      // ~minute timescale (real spokes live a few minutes to a few
      // orbital revolutions). 0.04 rad/s × ringAngle slows the
      // pulsing cycle so neighbouring sectors fade out of phase.
      float spokeLifecycle = 0.5 + 0.5 * sin(spokeTime * 0.045 + ringAngle * 2.0);
      spokes *= smoothstep(0.10, 0.55, spokeLifecycle);
      // Darken the ring under spokes by up to ~35%. Multiplied by
      // the optical-depth proxy so they only show up where there's
      // ring material to BE darkened (Cassini Division / spoke
      // overlap is invisible because the gap has nothing to dim).
      finalColor *= 1.0 - spokes * 0.35 * spokeStrength * (realStrip > 0.5 ? min(tau, 1.0) : tau);
    }

    float alpha = layerAlpha * opacity * divisions;
    // Forward-scattered rings read as more opaque from this angle
    // (more particles in the line of sight effectively contributing),
    // so bump the alpha proportionally — but only for the bands that
    // ACTUALLY transmit light. Dense bands stay at their normal
    // alpha so they silhouette against the bright thin rings around
    // them rather than getting an alpha boost they don't deserve.
    alpha *= 1.0 + forwardScatter * 0.4 * transmission;
  #ifdef RING_PHOTOMETRY
    // What lies behind is dimmed along the slant path (e^(-tau/mu)), so the
    // B ring hides the stars and the planet darkens behind the C ring at a
    // low angle; the ring's own light is kept as it was (its colour scaled
    // by the face-on weight over the slant one).
    float slantA = 1.0 - pow(max(1.0 - texColor.a, 0.0), 1.0 / max(abs(viewSide), 0.02));
    float lightW = alpha;
    alpha = clamp(slantA, 0.0, 1.0);
    finalColor *= lightW / max(alpha, 1e-3);
  #endif
    alpha = clamp(alpha, 0.0, 1.0);

    if (alpha < 0.01) discard;
    gl_FragColor = vec4(finalColor, alpha);
  }
`;

// Simplified ring shader without complex shadows (for performance)
export const simpleRingVertexShader = `
  varying vec2 vUv;
  varying vec3 vNormal;
  varying vec3 vWorldPosition;

  void main() {
    vUv = uv;
    vNormal = normalize(normalMatrix * normal);
    vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const simpleRingFragmentShader = `
  uniform sampler2D ringTexture;
  uniform vec3 sunPosition;
  uniform float opacity;

  varying vec2 vUv;
  varying vec3 vNormal;
  varying vec3 vWorldPosition;

  void main() {
    // Radial UV mapping
    vec2 centeredUv = vUv - 0.5;
    float dist = length(centeredUv) * 2.0;

    // Sample texture radially
    vec2 radialUv = vec2(dist, 0.5);
    vec4 texColor = texture2D(ringTexture, radialUv);

    // Simple lighting
    vec3 toSun = normalize(sunPosition - vWorldPosition);
    float light = abs(dot(vNormal, toSun)) * 0.5 + 0.5;

    vec3 finalColor = texColor.rgb * light;
    float alpha = texColor.a * opacity;

    if (alpha < 0.01) discard;

    gl_FragColor = vec4(finalColor, alpha);
  }
`;

// Procedural ring color generator (fallback if texture unavailable)
export const proceduralRingFragmentShader = `
  ${OKLAB_MIX_GLSL}
  uniform float innerRadius;
  uniform float outerRadius;
  uniform vec3 ringColor1;
  uniform vec3 ringColor2;
  uniform vec3 sunPosition;
  uniform vec3 planetCenter;
  uniform float planetRadius;
  uniform float shadowStrength;
  uniform float opacity;
  // Per-planet sunlight multiplier (shared uniform contract; main.js
  // wires it). <= 0.001 means "unwired" → fall back to 1.0.
  uniform float uSunIntensity;
  // Sun angular radius from the planet (radians) for physical
  // penumbra width; 0 = unwired → legacy fixed edge.
  uniform float uSunAngularRadius;

  varying vec2 vUv;
  varying vec3 vNormal;
  varying vec3 vWorldPosition;

  // Simple noise function
  float hash(float n) { return fract(sin(n) * 43758.5453); }

  float noise(float x) {
    float i = floor(x);
    float f = fract(x);
    return mix(hash(i), hash(i + 1.0), smoothstep(0.0, 1.0, f));
  }

  void main() {
    vec2 centeredUv = vUv - 0.5;
    float dist = length(centeredUv) * 2.0;

    // Normalized distance
    float t = dist;

    // Create ring bands with noise
    float bands = noise(t * 50.0) * 0.5 + 0.5;
    float gaps = smoothstep(0.3, 0.35, noise(t * 20.0 + 10.0));
    gaps *= smoothstep(0.4, 0.45, noise(t * 15.0 + 5.0));

    // Mix ring colors in OKLCh — perceptual hue arc keeps the mid-
    // tones vivid instead of slicing through grey. Uranus' dim
    // grey-blue ↔ darker-grey mix barely notices; Saturn's warm
    // tan ↔ cool ice mix shows the cleanest improvement.
    vec3 color = oklchMix(ringColor1, ringColor2, bands);

    // Apply gaps
    float alpha = gaps * opacity;

    // Fade at edges
    alpha *= smoothstep(0.0, 0.1, t) * smoothstep(1.0, 0.8, t);

    // Simple lighting. Only the direct sun term scales with the
    // per-planet sunlight multiplier; the 0.5 floor stays as-is.
    float sunI = uSunIntensity > 0.001 ? uSunIntensity : 1.0;
    vec3 toSun = normalize(sunPosition - vWorldPosition);
    float light = abs(dot(vNormal, toSun)) * 0.5 * sunI + 0.5;
    color *= light;

    // Planet shadow: same straight cylinder model used by Saturn's
    // textured rings, kept a little softer for Uranus' faint dark rings.
    float shadowFactor = 1.0;
    vec3 shadowAxisVector = planetCenter - sunPosition;
    float shadowAxisLength = length(shadowAxisVector);
    if (shadowAxisLength > 0.0001) {
      vec3 cylAxis = shadowAxisVector / shadowAxisLength;
      vec3 planetToFrag = vWorldPosition - planetCenter;
      float shadowT = dot(planetToFrag, cylAxis);
      vec3 shadowPerp = planetToFrag - cylAxis * shadowT;
      float shadowPerpDist = length(shadowPerp);
      if (shadowT > 0.0) {
        float halfW = uSunAngularRadius > 0.0
          ? max(planetRadius * 0.02, shadowT * uSunAngularRadius)
          : planetRadius * 0.02;
        shadowFactor = smoothstep(planetRadius - halfW, planetRadius + halfW, shadowPerpDist);
      }
    }
    color *= 1.0 - (1.0 - shadowFactor) * shadowStrength;

    if (alpha < 0.01) discard;

    gl_FragColor = vec4(color, alpha);
  }
`;

// Saturn's dusty rings: the narrow G ring at 2.76 Saturn radii and the broad
// E ring, densest at Enceladus's orbit (3.95 radii), whose geysers feed it,
// and thinning out past 8. Their grains are about a micron across, so they
// throw light forward: seen with the Sun behind them (Cassini's "In Saturn's
// Shadow", 2006, and "The Day the Earth Smiled", 2013) they glow, and from
// the Sun's side they are all but invisible, as they are in the viewer's
// usual views. Brightness is a Henyey-Greenstein lobe (g 0.7) on the angle
// between the view and the sunlight, dark inside Saturn's shadow cylinder.
// Drawn additively over the main rings, in the same tilt group.
//
// The plane stands for a slab of dust: the E ring is 4,000 to 5,000 km thick
// (FWHM) at Enceladus's orbit (Hedman et al. 2012, Icarus 217, 322), and the
// moons inside it sit in that slab. Where the view ray meets one of them
// (uOccluders, in view space) only the dust between the camera and the moon
// lies in front of it, so the plane's light there is cut to that share of the
// ray's path through the slab. Drawn whole, it laid the full column over the
// near half of Enceladus and the moon lost the top or bottom of its outline.
export const saturnDustRingVertexShader = `
  varying vec3 vWorldPosition;
  varying vec3 vViewPos;
  varying vec3 vPlaneN;
  varying vec3 vPlaneC;
  varying float vRadii;
  uniform float uBodyRadius;
  void main() {
    vRadii = length(position.xy) / uBodyRadius;
    vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vViewPos = mv.xyz;
    vPlaneN = normalize(normalMatrix * vec3(0.0, 0.0, 1.0));
    vPlaneC = (modelViewMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
    gl_Position = projectionMatrix * mv;
  }
`;

export const saturnDustRingFragmentShader = `
  varying vec3 vWorldPosition;
  varying vec3 vViewPos;
  varying vec3 vPlaneN;
  varying vec3 vPlaneC;
  varying float vRadii;
  uniform vec3 sunPosition;
  uniform vec3 planetCenter;
  uniform float planetRadius;
  uniform float uSunIntensity;
  uniform float uGain;
  // Half the slab's thickness in Saturn radii (2,250 km).
  uniform float uSlabHalf;
  uniform vec4 uOccluders[4];
  uniform int uOccluderCount;

  // The share of this ray's path through the dust slab that lies in front of
  // the nearest moon it meets (1 when it meets none).
  float dustInFront() {
    float tHit = length(vViewPos);
    vec3 rd = vViewPos / tHit;
    float H = uSlabHalf * planetRadius;
    float h = -dot(vPlaneC, vPlaneN);
    float sz = dot(rd, vPlaneN);
    sz = abs(sz) < 1e-6 ? (sz < 0.0 ? -1e-6 : 1e-6) : sz;
    float t1 = (H - h) / sz;
    float t2 = (-H - h) / sz;
    float tEntry = max(min(t1, t2), 0.0);
    float tExit = max(max(t1, t2), tEntry + 1e-9);
    float total = tExit - tEntry;
    float w = 1.0;
    for (int i = 0; i < 4; i++) {
      if (i >= uOccluderCount) break;
      vec3 c = uOccluders[i].xyz;
      float r = uOccluders[i].w;
      float b = dot(rd, c);
      float disc = b * b - (dot(c, c) - r * r);
      if (disc <= 0.0) continue;
      float tMoon = b - sqrt(disc);
      if (tMoon <= 0.0) continue;
      w = min(w, clamp((tMoon - tEntry) / total, 0.0, 1.0));
    }
    return w;
  }

  void main() {
    float r = vRadii;
    // The G ring: about 7,000 km wide round 166,000 km.
    float gRing = exp(-pow((r - 2.76) / 0.045, 2.0));
    // The E ring: rising from 3 radii to its peak at Enceladus, then a long
    // tail. Relative brightnesses after the Cassini high-phase mosaics.
    float eRing = smoothstep(3.0, 3.95, r) * exp(-max(r - 3.95, 0.0) / 1.5) * (1.0 - smoothstep(7.0, 8.5, r));
    float profile = 0.55 * gRing + 0.32 * eRing;
    if (profile < 1e-4) discard;

    vec3 viewDir = normalize(cameraPosition - vWorldPosition);
    vec3 sunDir = normalize(sunPosition - vWorldPosition);
    // Scattering angle: 1 when the camera looks toward the Sun through the dust.
    float c = -dot(viewDir, sunDir);
    float g = 0.7;
    float hg = (1.0 - g * g) / pow(1.0 + g * g - 2.0 * g * c, 1.5);

    // Saturn's shadow: a cylinder down-sun of the planet.
    vec3 axis = normalize(planetCenter - sunPosition);
    vec3 rel = vWorldPosition - planetCenter;
    float along = dot(rel, axis);
    float across = length(rel - axis * along);
    float lit = along > 0.0 ? smoothstep(planetRadius * 0.98, planetRadius * 1.02, across) : 1.0;

    float sunI = uSunIntensity > 0.001 ? uSunIntensity : 1.0;
    // A faint blue: the E ring's grains are small enough to scatter blue
    // light more than red.
    vec3 colour = vec3(0.78, 0.86, 1.0);
    gl_FragColor = vec4(colour * profile * hg * lit * sunI * uGain * dustInFront(), 1.0);
  }
`;
